import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import {
  Box,
  Button,
  Modal,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { tyrioUI } from '@tyrio/ui-library';
import { DBOrderItems, DBProductApi } from '@tyrio/dto';
import api from '@tyrio/api-factory';

interface SearchReplaceModalProps {
  open: boolean;
  onClose: () => void;
  onItemSelected: (item: DBOrderItems) => void;
  originalItem: DBOrderItems | null;
}

interface SearchResult {
  id: string;
  ean: string;
  sku: string;
  ipc: string;
  productName: string;
  brand: string;
  price: number;
}

const searchTypeOptions = [
  { value: 'ean', label: 'EAN' },
  { value: 'manufacturerCode', label: 'Manufacturer Code' },
  { value: 'productName', label: 'Product Name' },
  { value: 'sku', label: 'SKU' },
];

export const SearchReplaceModal: React.FC<SearchReplaceModalProps> = ({
  open,
  onClose,
  onItemSelected,
  originalItem,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('ean');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setSearchResults([]);
    }
  }, [open]);

  const performSearch = async () => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    try {
      let results: SearchResult[] = [];

      if (searchType === 'ean' && searchQuery.length === 13) {
        // For EAN search, use the specific get_product_by_ean endpoint
        try {
          const response = await api.fetch<{
            response: DBProductApi['getOne']['response'];
            requestBody: never;
            request: { ean: string };
          }>('get_product_by_ean', { ean: searchQuery });

          if (response) {
            results.push({
              id: response.uid,
              ean: response.ean,
              sku: response.sku || '',
              ipc: response.manufacturerCode || '',
              productName: response.productName,
              brand: response.model?.brand || '',
              price: 0,
            });
          }
        } catch (error) {
          // Product not found with exact EAN, no results
          console.log('Product not found with EAN:', searchQuery);
        }
      } else {
        // For other search types, get all products and filter manually
        // This is a temporary solution until the products API search is properly implemented
        const response = await api.fetch<DBProductApi['list']>('products', {
          pageSize: '100', // Get more products to search through
          page: '1',
        });

        const searchLower = searchQuery.toLowerCase();
        
        response.data.forEach((product: any) => {
          let matchFound = false;
          
          if (searchType === 'ean' && product.ean?.toLowerCase().includes(searchLower)) {
            matchFound = true;
          } else if (searchType === 'manufacturerCode' && product.manufacturerCode?.toLowerCase().includes(searchLower)) {
            matchFound = true;
          } else if (searchType === 'productName' && product.productName?.toLowerCase().includes(searchLower)) {
            matchFound = true;
          } else if (searchType === 'sku' && product.sku?.toLowerCase().includes(searchLower)) {
            matchFound = true;
          }
          
          if (matchFound) {
            results.push({
              id: product.uid,
              ean: product.ean,
              sku: product.sku || '',
              ipc: product.manufacturerCode || '',
              productName: product.productName,
              brand: product.model?.brand || '',
              price: 0,
            });
          }
        });

        // Limit results to prevent UI performance issues
        results = results.slice(0, 20);
      }

      setSearchResults(results);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      performSearch();
    }
  };

  const handleItemClick = (searchResult: SearchResult) => {
    if (!originalItem) return;

    const newItem: DBOrderItems = {
      ...originalItem,
      ean: searchResult.ean,
      sku: searchResult.sku,
      manufacturerCode: searchResult.ipc,
      productName: searchResult.productName,
      brand: searchResult.brand,
      sellingPrice: searchResult.price || originalItem.sellingPrice, // Keep original price if no price found
    };

    onItemSelected(newItem);
  };

  const formatPrice = (price: number) => {
    return price.toLocaleString('de-DE', {
      style: 'currency',
      currency: 'EUR',
      currencyDisplay: 'symbol',
    });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '600px',
          maxWidth: '90vw',
          bgcolor: 'background.paper',
          borderRadius: '16px',
          boxShadow: 24,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          padding: '24px',
        }}
      >
        <CloseWrapper>
          <CloseIcon
            onClick={onClose}
            sx={{ cursor: 'pointer' }}
          />
        </CloseWrapper>

        <TitleWrapper>SEARCH & REPLACE</TitleWrapper>
        
        {originalItem && (
          <OriginalItemContainer>
            <Typography variant="body2" color={tyrioUI.colors.black.B50}>
              Replacing: <strong>{originalItem.brand} {originalItem.productName}</strong>
            </Typography>
          </OriginalItemContainer>
        )}

        <SearchContainer>
          <FormControl fullWidth margin="normal">
            <InputLabel>Search Type</InputLabel>
            <Select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              label="Search Type"
            >
              {searchTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            margin="normal"
            label="Search products"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyPress}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {isSearching ? <CircularProgress size={20} /> : <SearchIcon />}
                </InputAdornment>
              ),
            }}
          />
          <Typography variant="caption" color={tyrioUI.colors.black.B50} style={{ marginLeft: '8px' }}>
            Press Enter to search
          </Typography>
        </SearchContainer>

        <SearchResultsContainer>
          <Typography variant="h6" gutterBottom>
            Search Results
          </Typography>
          
          {searchResults.length === 0 && searchQuery && !isSearching && (
            <EmptyState>
              <Typography variant="body2" color={tyrioUI.colors.black.B50}>
                No products found for "{searchQuery}"
              </Typography>
            </EmptyState>
          )}

          {searchResults.length === 0 && !searchQuery && (
            <EmptyState>
              <Typography variant="body2" color={tyrioUI.colors.black.B50}>
                Enter a search term to find products
              </Typography>
            </EmptyState>
          )}

          {searchResults.length > 0 && (
            <SearchResultsList>
              {searchResults.map((result) => (
                <SearchResultItem
                  key={result.id}
                  product={result}
                  onSelect={() => handleItemClick(result)}
                />
              ))}
            </SearchResultsList>
          )}
        </SearchResultsContainer>

        <ButtonsWrapper>
          <Button
            color="info"
            style={{
              width: '100%',
              height: '50px',
              border: '1px solid rgba(25, 118, 210, 1)',
            }}
            onClick={onClose}
          >
            CANCEL
          </Button>
        </ButtonsWrapper>
      </Box>
    </Modal>
  );
};

const CloseWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: end;
`;

const TitleWrapper = styled.div`
  font-family: Barlow;
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 16px;
`;

const OriginalItemContainer = styled.div`
  background-color: #f4f6f8;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const SearchContainer = styled.div`
  margin-bottom: 16px;
`;

const SearchResultsContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const SearchResultsList = styled(List)`
  flex: 1;
  overflow-y: auto;
  border: 1px solid ${tyrioUI.colors.black.B40};
  border-radius: 8px;
  margin: 0;
  padding: 0;
`;

const SearchResultItem: React.FC<{
  product: SearchResult;
  onSelect: () => void;
}> = ({ product, onSelect }) => {
  return (
    <ResultItemWrapper onClick={onSelect}>
      <ProductTitle>{product.brand} {product.productName}</ProductTitle>
      <ProductDetails>
        <div>EAN: {product.ean}</div>
        <div>IPC: {product.ipc}</div>
        <div>SKU: {product.sku}</div>
      </ProductDetails>
    </ResultItemWrapper>
  );
};

const ResultItemWrapper = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${tyrioUI.colors.black.B40};
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const ProductTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;
  color: ${tyrioUI.colors.black.B100};
`;

const ProductDetails = styled.div`
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #919eab;
  
  @media (max-width: 600px) {
    flex-direction: column;
    gap: 4px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  border: 1px solid ${tyrioUI.colors.black.B40};
  border-radius: 8px;
  background-color: #f9f9f9;
`;

const ButtonsWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
`; 